import React, { useEffect, useState } from "react";
import Card from "components/card";
import { useParams, useNavigate } from "react-router-dom";
import { getUser, changeUserRole, adminResetPassword } from "services/users";
import { useToast } from "components/Toast";

const AdminUserShow: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const result: any = await getUser(Number(id));
        // backend may return { data: {...} } or the user object directly
        const u = result?.data ? result.data : result;
        setUser(u || null);
      } catch (err) {
        console.error(err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleRoleChange = async (role: string) => {
    if (!user) return;
    try {
      await changeUserRole(user.id, role);
      setUser((prev: any) => ({ ...(prev || {}), role }));
      toast.showSuccess("Role updated");
    } catch (err) {
      console.error(err);
      toast.showError("Failed to change role");
    }
  };

  const handleReset = async () => {
    if (!user) return;
    if (!window.confirm("Send password reset email to this user?")) return;
    try {
      await adminResetPassword(user.id);
      toast.showSuccess("Password reset sent");
    } catch (err) {
      console.error(err);
      toast.showError("Failed to send password reset");
    }
  };

  if (loading)
    return (
      <Card extra="w-full p-4">
        <div>Loading...</div>
      </Card>
    );

  if (!user)
    return (
      <Card extra="w-full p-4">
        <div>User not found.</div>
      </Card>
    );

  return (
    <Card extra="w-full p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl font-bold">User Details</h2>
        <div>
          <button
            onClick={() => navigate(`/admin/users/${user.id}/edit`)}
            className="mr-2 text-indigo-600 dark:text-indigo-300"
          >
            Edit
          </button>
          <button
            onClick={handleReset}
            className="text-sm text-gray-600 dark:text-gray-300"
          >
            Reset Password
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <div className="text-sm text-gray-500">Name</div>
          <div className="font-medium">
            {`${user.firstname || ""} ${user.lastname || ""}`.trim() ||
              user.name}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Email</div>
          <div className="font-medium">{user.email}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Role</div>
          <div className="font-medium">
            <select
              value={user.role || "user"}
              onChange={(e) => handleRoleChange(e.target.value)}
              className="input"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Joined</div>
          <div className="font-medium">
            {user.created_at ? new Date(user.created_at).toLocaleString() : "-"}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="text-sm text-gray-500 dark:text-gray-300">Meta</div>
          <pre className="mt-2 max-h-40 overflow-auto rounded bg-gray-50 p-2 text-xs dark:bg-navy-700 dark:text-white">
            {JSON.stringify(user, null, 2)}
          </pre>
        </div>
      </div>
    </Card>
  );
};

export default AdminUserShow;
